import { FinancialSpecsData } from "@mstys/raport-auta-client-front";
import type {
  Evaluation,
  EquipmentData,
  EquipmentPacket,
  Equipment,
} from "./report";
import { EquipmentPackage } from "@/components/RaportHSP/RaportHSPEstimation/CarFinancialDetails.types";

/**
 * Sprawdza czy wartość jest poprawna (nie null, nie undefined, nie pusta)
 * @param value - Wartość do sprawdzenia
 * @returns true jeśli wartość jest poprawna, false w przeciwnym razie
 */
const isValidValue = (value: unknown): boolean => {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    !Number.isNaN(value)
  );
};

/**
 * Mapuje pakiety wyposażenia z API na format wymagany przez komponent HSPFinancialSpecs
 * @param packets - Tablica pakietów wyposażenia z API
 * @returns Tablica pakietów w formacie wymaganym przez komponent
 */
const mapEquipmentPacketsToPackages = (
  packets: EquipmentPacket[]
): EquipmentPackage[] => {
  return packets
    .filter(
      (packet): packet is EquipmentPacket & { currentMarketPrice: number } =>
        isValidValue(packet.currentMarketPrice)
    )
    .map((packet) => ({
      name: packet.name,
      code: packet.code,
      price: packet.currentMarketPrice?.toString() || "0",
    }));
};

/**
 * Oblicza łączną wartość wyposażenia (equipments + packets) na podstawie aktualnej wartości rynkowej
 * @param equipments - Tablica elementów wyposażenia
 * @param packets - Tablica pakietów wyposażenia
 * @returns Suma wartości rynkowych wszystkich elementów
 */
const calculateTotalEquipmentValue = (
  equipments: Equipment[],
  packets: EquipmentPacket[]
): number => {
  const equipmentsTotal = equipments.reduce((sum, equipment) => {
    const marketPrice =
      isValidValue(equipment.currentMarketPrice) &&
      typeof equipment.currentMarketPrice === "number"
        ? equipment.currentMarketPrice
        : 0;
    return sum + marketPrice;
  }, 0);

  const packetsTotal = packets.reduce((sum, packet) => {
    const marketPrice =
      isValidValue(packet.currentMarketPrice) &&
      typeof packet.currentMarketPrice === "number"
        ? packet.currentMarketPrice
        : 0;
    return sum + marketPrice;
  }, 0);

  return equipmentsTotal + packetsTotal;
};

/**
 * Oblicza wartość wyposażenia opcjonalnego (tylko pojedyncze equipments, bez pakietów)
 * @param equipments - Tablica elementów wyposażenia
 * @returns Suma aktualnych wartości rynkowych wszystkich elementów
 */
const calculateOptionalEquipmentPrice = (equipments: Equipment[]): number => {
  return equipments.reduce((sum, equipment) => {
    const marketPrice =
      isValidValue(equipment.currentMarketPrice) &&
      typeof equipment.currentMarketPrice === "number"
        ? equipment.currentMarketPrice
        : 0;
    return sum + marketPrice;
  }, 0);
};

/**
 * Oblicza łączną cenę pakietów wyposażenia
 * @param packages - Tablica pakietów wyposażenia
 * @returns Suma cen wszystkich pakietów
 */
const calculatePackagesPrice = (packages: EquipmentPackage[]): number => {
  return packages.reduce((sum, pkg) => sum + Number(pkg.price), 0);
};

/**
 * Mapuje dane finansowe z API na format wymagany przez komponent HSPFinancialSpecs
 * @param evaluation - Dane wyceny pojazdu z API
 * @param equipment - Dane wyposażenia pojazdu z API
 * @returns Zmapowane dane finansowe lub null jeśli brak wymaganych danych
 */
export const mapFinancialData = (
  evaluation?: Evaluation,
  equipment?: EquipmentData
): FinancialSpecsData | null => {
  // Jeśli brak danych evaluation, nie możemy stworzyć danych finansowych
  if (!evaluation || !isValidValue(evaluation.baseCarValue)) {
    return null;
  }

  const basePrice = evaluation.baseCarValue;
  const standardEquipments = equipment?.standard?.equipments || [];
  const standardPackets = equipment?.standard?.packets || [];

  const optionalEquipments = equipment?.optional?.equipments || [];
  const optionalPackets = equipment?.optional?.packets || [];

  // Mapowanie pakietów
  const optionalPackages = mapEquipmentPacketsToPackages(optionalPackets);

  // docelowo brane z API !
  const optionalEquipmentPrice =
    equipment?.optional?.summary?.equipmentsTotalGrossPrice ||
    calculateOptionalEquipmentPrice(optionalEquipments);

  // docelowo brane z API !
  const optionalPackagesPrice =
    equipment?.optional?.summary?.packetsTotalGrossPrice ||
    calculatePackagesPrice(optionalPackages);

  // Obliczenie łącznej wartości wyposażenia dodatkowego (equipments + packets)
  const totalAdditionalEquipmentValue = calculateTotalEquipmentValue(
    optionalEquipments,
    optionalPackets
  );
  const totalStandardEquipmentValue = calculateTotalEquipmentValue(
    standardEquipments,
    standardPackets
  );

  // Obliczenie procentu wartości wyposażenia w stosunku do ceny bazowej
  const equipmentPercentage =
    basePrice > 0 ? (totalAdditionalEquipmentValue / basePrice) * 100 : 0;

  // Czy ma wyposażenie opcjonalne
  const hasOptionalEquipment = optionalEquipments.length > 0;
  const hasPackages = optionalPackages.length > 0;

  return {
    basePrice,
    standardPackets: standardPackets.map((packet) => ({
      name: packet.name,
      code: packet.code,
      price: packet.catalogPrice?.toString() || "0",
      equipments: packet.equipments,
    })),
    standardEquipments: standardEquipments,
    optionalPackets: optionalPackets.map((packet) => ({
      name: packet.name,
      code: packet.code,
      price: packet.catalogPrice?.toString() || "0",
      equipments: packet.equipments,
    })),
    optionalEquipments: optionalEquipments,
    optionalEquipmentPrice,
    optionalPackagesPrice,
    totalAdditionalEquipmentValue,
    totalStandardEquipmentValue,
    equipmentPercentage,
    hasOptionalEquipment,
    hasPackages,
  };
};

/**
 * Tworzy dane finansowe na podstawie danych z API
 * Funkcja wrapper dla mapFinancialData
 * @param evaluation - Dane wyceny pojazdu z API
 * @param equipment - Dane wyposażenia pojazdu z API
 * @returns Zmapowane dane finansowe lub null jeśli brak wymaganych danych
 */
export const createFinancialData = (
  evaluation?: Evaluation,
  equipment?: EquipmentData
): FinancialSpecsData | null => {
  return mapFinancialData(evaluation, equipment);
};
